import React, { useState, useEffect, useRef } from "react";
import { Button, message, Space, Tooltip } from "antd";
import { RedoOutlined, SearchOutlined, HomeOutlined } from "@ant-design/icons";
import axios from "axios";
import Logger from "@/utility/logger";

import ProjectDetails from "@/components/ProjectDetails";
import SectionGeneratorPanel from "@/components/SectionGeneratorPanel/SectionGeneratorPanel";
import DialogPopup from "@/components/DialogPopup/DialogPopup";
import DocumentReadinessModal from "@/components/DocumentReadinessModal/DocumentReadinessModal";
import { useFontStyle } from "@/context/StyleContext";
import SectionPageButtonContainer from "./SectionPageButtonContainer/SectionPageButtonContainer";
import {
  appendTaskUpdateIfNew,
  createActiveSectionTask,
  getSectionActiveTask,
  releaseSectionTaskOnFailure,
} from "@/utility/sectionTaskState";
import { isMissingSectionBookmarkError } from "@/utility/wordBookmarkErrors";
import { extractGroundingArtifactFromResponseData } from "@/utility/groundingMarkers";

const SectionPage = ({
  trackChanges,
  handleTrackChangesToggle,
  // insertTextAtSection,
  insertAtSection,
  hasSectionBookmarks,
  scrollToSection,
  // createTable,
  getSelectedText,
  selectedSections,
  setSelectedSections,
  generatedSections,
  setGeneratedSections,
  sectionStatus,
  sections,
  templateTagsMap,
  selectedProject,
  selectedReportTemplate,
  sectionEvalStatus,
  setSectionEvalStatus,
  runTask,
  selectAllSections,
  setSelectAllSections,
  acceptChanges,
  declineChanges,
  outputDecision,
  setOutputDecision,
  sectionDialogContent,
  setSectionDialogContent,
  sourceDialogOpen,
  setSourceDialogOpen,
  sourceDialogContent,
  setSourceDialogContent,
  feedbackDialogOpen,
  setFeedbackDialogOpen,
  feedbackDialogContent,
  setFeedbackDialogContent,
  regenerateDialogOpen,
  setRegenerateDialogOpen,
  regenerateDialogContent,
  setRegenerateDialogContent,
  toggleRegenerateOptions,
  regenerateLoading,
  setRegenerateLoading,
  qualityCheckDialogOpen,
  setQualityCheckDialogOpen,
  qualityCheckDialogContent,
  setQualityCheckDialogContent,
  //formatDateToLongForm,
  bookmarks,
  projectDetailsExpanded,
  setProjectDetailsExpanded,
  projectPlaceholderDetails,
  messageChatId,
  selectedText,
  retrievellmOuputWOGroupAsync,
  retrieveSourceDocument,
  uploadedFiles,
  retreiveSourceDocumentMetadata,
  uploadDocument,
  deleteDocument,
  deleteTag,
  setShowError,
  titleMap,
  setTest,
  setActiveTabId,
  location,
  uploadedAgentFiles,
  setUploadedAgentFiles,
  transcribeAudio,
  retrieveAgentQuery,
  retrieveAgentStatus,
  retrieveChatHistory,
  retrieveChatAsyncTask,
  waitForChatAsyncResult,
  sectionGeneration,
  setSectionGeneration,
  handleAgentStream,
  agenticRegenerationRequest,
  handleAIOutput,
  handleSectionGeneration,
  setIsAgentSectionRegenerationOn,
  setAgenticRegenerationRequest,
  parseMarkdownSections,
  retrieveDocumentsList,
  retrieveDocumentsForImageInsertion,
  retrieveDocumentImages,
  downloadDocumentImage,
  submitLangfuseFeedback,
  handleCurrentDocxUpload,
  setSectionStatus,
  clearGroundingForSection,
  tryClaimAgenticRegenerationRequest,
  releaseAgenticRegenerationRequest,
  logAuditAction,
  reportTemplateProjectId,
  onGraphViewControllerChange,
  groundingByTaskId,
  groundingLoadingTaskIds,
  groundingErrorsByTaskId,
  fetchGroundingArtifact,
  documentReadinessNotice,
  setDocumentReadinessNotice,
  clearDocumentReadinessNotice,
  insertionLockRef,
}) => {
  const [feedbackStates, setFeedbackStates] = useState<
    Record<
      string,
      {
        thumbs: boolean | null;
        comment: string;
        isSubmitted: boolean;
        showCommentBox: boolean;
        isSubmitting?: boolean;
      }
    >
  >({});

  const { draftStyles } = useFontStyle();
  const agentReconnectionAttempted = useRef(false);
  const resumedSectionTaskIds = useRef<Set<string>>(new Set());
  const generatedSectionsRef = useRef(generatedSections);
  generatedSectionsRef.current = generatedSections;
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const appendChatAsyncUpdate = (section, data) => {
    const status = data?.status || "running";
    const message =
      data?.message || (status?.toLowerCase() === "completed" ? "Task complete." : data?.chat_task_status || status);
    if (!section?.id || !message) return;

    const newUpdate = {
      message,
      status,
      task_id: data?.task_id,
      chat_task_status: data?.chat_task_status,
      event_seq: data?.event_seq,
      updated_at: data?.updated_at,
    };

    setGeneratedSections((prevSections) =>
      prevSections.map((generatedSection) => {
        if (generatedSection.id !== section.id) return generatedSection;

        const updates = generatedSection.agentUpdates || [];
        const lastUpdate = updates[updates.length - 1];
        if (
          (newUpdate.event_seq && lastUpdate?.event_seq === newUpdate.event_seq) ||
          (!newUpdate.event_seq && lastUpdate?.message === newUpdate.message && lastUpdate?.status === newUpdate.status)
        ) {
          return generatedSection;
        }

        const nextUpdates = appendTaskUpdateIfNew(updates, newUpdate);
        if (nextUpdates === updates) {
          return generatedSection;
        }

        return {
          ...generatedSection,
          agentUpdates: nextUpdates,
          chat_async_task_id: data?.task_id || generatedSection.chat_async_task_id,
          activeSectionTask:
            createActiveSectionTask(
              "chat_async",
              generatedSection.activeSectionTask?.mode ||
                (generatedSection.chatAsyncRegenerationInProgress ? "regeneration" : "generation"),
              data?.task_id || generatedSection.activeSectionTask?.taskId || generatedSection.chat_async_task_id
            ) || generatedSection.activeSectionTask,
        };
      })
    );
  };

  useEffect(() => {
    const handleOnline = () => {
      Logger.debug("SectionPage.tsx: Connection is back ONLINE.");
      setIsOnline(true);
    };

    const handleOffline = () => {
      Logger.debug("SectionPage.tsx: Connection is OFFLINE.");
      setIsOnline(false);
      setSectionGeneration(false);
      agentReconnectionAttempted.current = false;
      resumedSectionTaskIds.current.clear();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOnline && sectionGeneration) {
      Logger.debug("SectionPage.tsx: Went offline → stopping section generation.");
      setSectionGeneration(false);
      agentReconnectionAttempted.current = false;
      resumedSectionTaskIds.current.clear();
    }
  }, [isOnline, sectionGeneration]);

  useEffect(() => {
    /*
     * This effect runs once after coming back online to check for and resume any agentic (streaming)
     * Tasks that were interrupted by the disconnection / reload of the word document / reload of the word addin
     * It checks if =>
     * We are not actively generating a section currently (!sectionGeneration)
     * There exists generated sections we need to check (generatedSections.length > 0)
     * The user has an active internet connection now (isOnline)
     * This agent re-connection has not already been run (!agentReconnectionAttempted.current) to prevent re-running it
     * There is a project selected (selectedProject)
     */
    if (
      !sectionGeneration &&
      generatedSectionsRef.current.length > 0 &&
      isOnline &&
      !agentReconnectionAttempted.current &&
      selectedProject
    ) {
      agentReconnectionAttempted.current = true;
      Logger.debug("SectionPage.tsx: Online and has un-checked sections. Running agent status check...");

      const resumeAgentTasks = async () => {
        const stuckSections = generatedSectionsRef.current.filter((section) => {
          const type = String(section?.type || "").toLowerCase();
          if (type === "section") {
            return (
              (section.chatAsyncInProgress || section.chatAsyncRegenerationInProgress) && !section.chat_async_task_id
            );
          }
          if (type === "section_agentic") {
            return (section.agenticInProgress || section.agenticRegenerationInProgress) && !section.task_id;
          }
          return false;
        });

        if (stuckSections.length > 0) {
          setGeneratedSections((prevSections) =>
            prevSections.map((s) => {
              const isStuck = stuckSections.some((stuck) => stuck.id === s.id);
              if (!isStuck) return s;

              return {
                ...s,
                chatAsyncInProgress: false,
                chatAsyncRegenerationInProgress: false,
                agenticInProgress: false,
                agenticRegenerationInProgress: false,
                activeSectionTask: undefined,
                agentUpdates: appendTaskUpdateIfNew(s.agentUpdates || [], {
                  message: "Generation interrupted before starting. Please regenerate.",
                  status: "error",
                }),
              };
            })
          );
        }

        const resumableTasks = generatedSectionsRef.current
          .map((section) => ({ section, task: getSectionActiveTask(section) }))
          .filter(({ task }) => Boolean(task) && !resumedSectionTaskIds.current.has(task.taskId));

        if (resumableTasks.length === 0) {
          Logger.debug("SectionPage.tsx: No resumable section tasks found.");
          agentReconnectionAttempted.current = false;
          return;
        }

        resumableTasks.forEach(({ task }) => {
          resumedSectionTaskIds.current.add(task.taskId);
        });

        const markSectionTaskFailed = (section, message, taskId) => {
          setGeneratedSections((prevSections) =>
            prevSections.map((s) => {
              if (s.id !== section.id) return s;

              const updates = s.agentUpdates || [];
              return {
                ...s,
                activeSectionTask: undefined,
                chatAsyncInProgress: false,
                chatAsyncRegenerationInProgress: false,
                task_id: String(s.type || "").toLowerCase() === "section_agentic" ? null : s.task_id,
                agenticInProgress: false,
                agenticRegenerationInProgress: false,
                agentUpdates: appendTaskUpdateIfNew(updates, { message, status: "error", task_id: taskId }),
              };
            })
          );
        };

        const handleFailedInsertion = (section, task) => {
          setGeneratedSections((prev) =>
            prev.map((s) =>
              s.id === section.id
                ? {
                    ...s,
                    activeSectionTask:
                      s.activeSectionTask || createActiveSectionTask(task.kind, task.mode, task.taskId),
                    ...(task.kind === "chat_async" && {
                      chatAsyncInProgress: true,
                      chatAsyncRegenerationInProgress: task.mode === "regeneration",
                    }),
                    ...(task.kind === "agentic" && {
                      agenticInProgress: true,
                      agenticRegenerationInProgress: task.mode === "regeneration",
                    }),
                    chat_history_id: null,
                    agentUpdates: appendTaskUpdateIfNew(s.agentUpdates || [], {
                      message: "Failed to insert content into document. Will retry when online.",
                      status: "error",
                      task_id: task.taskId,
                    }),
                  }
                : s
            )
          );
        };

        const resumeChatAsyncTask = async (section, task) => {
          Logger.debug("SectionPage.tsx: Reconnecting to chat-async stream for task:", task.taskId);

          appendChatAsyncUpdate(section, {
            status: "running",
            task_id: task.taskId,
            message: "Reconnecting to section generation...",
            chat_task_status: "RECONNECTING",
          });

          let llmResponse: any = {};

          try {
            const chatTask = await retrieveChatAsyncTask(task.taskId);
            const taskStatus = chatTask?.status?.toLowerCase();

            if (taskStatus === "completed") {
              if (!chatTask.chat_history_id) {
                throw new Error(`Async chat task ${task.taskId} completed without chat history id`);
              }

              llmResponse = await retrieveChatHistory(chatTask.chat_history_id);
              appendChatAsyncUpdate(section, {
                status: "completed",
                task_id: task.taskId,
                message: "Task complete.",
              });
            } else if (taskStatus === "failed") {
              throw new Error(chatTask.error_message || "Async chat task failed");
            } else {
              llmResponse = await waitForChatAsyncResult(task.taskId, (event) => {
                appendChatAsyncUpdate(section, event);
              });
            }
          } catch (streamError) {
            Logger.error("SectionPage.tsx: Chat async reconnection failed:", section.id, streamError);
            setGeneratedSections((prev) =>
              prev.map((s) =>
                s.id === section.id
                  ? {
                      ...s,
                      agentUpdates: appendTaskUpdateIfNew(s.agentUpdates || [], {
                        message: "Reconnection failed. Please try again.",
                        status: "error",
                        task_id: task.taskId,
                      }),
                    }
                  : s
              )
            );
            return;
          }

          const key = `${section.insertion_key}`;
          if (insertionLockRef?.current.has(key)) {
            Logger.debug(`SectionPage.tsx: Skipping resumeChatAsyncTask insertion for key ${key} - already locked.`);
            return;
          }
          insertionLockRef?.current.add(key);
          try {
            await declineChanges(section.insertion_key);
            await handleAIOutput(llmResponse, section, true);
          } catch (error) {
            if (isMissingSectionBookmarkError(error)) {
              message.warning(error.message);
              setGeneratedSections((prevSections) =>
                prevSections.map((s) =>
                  s.id === section.id
                    ? {
                        ...s,
                        activeSectionTask: undefined,
                        chatAsyncInProgress: false,
                        chatAsyncRegenerationInProgress: false,
                        agenticInProgress: false,
                        agenticRegenerationInProgress: false,
                      }
                    : s
                )
              );
              return;
            }

            Logger.error("SectionPage.tsx: Chat async insertion failed for", section.id, error);
            handleFailedInsertion(section, task);
          } finally {
            insertionLockRef?.current.delete(key);
          }
        };

        const resumeAgenticTask = async (section, task) => {
          Logger.debug("SectionPage.tsx: Checking agent task status for section:", section.id);

          setGeneratedSections((prevSections) =>
            prevSections.map((s) => {
              if (s.id !== section.id) return s;

              return {
                ...s,
                task_id: task.taskId,
                activeSectionTask: s.activeSectionTask || createActiveSectionTask("agentic", task.mode, task.taskId),
                agenticRegenerationInProgress: task.mode === "regeneration",
                agenticInProgress: task.mode === "generation",
                agentUpdates: appendTaskUpdateIfNew(s.agentUpdates || [], {
                  message: "Reconnecting to agent...",
                  status: "pending",
                  task_id: task.taskId,
                }),
              };
            })
          );

          let llmResponse: any = {};

          try {
            const status = await retrieveAgentStatus(task.taskId);
            const taskStatus = status?.data?.status?.toLowerCase();
            Logger.debug("SectionPage.tsx: Agent task status for", section.id, "is", status?.data?.status);

            if (taskStatus === "completed") {
              const responseData = status?.data?.response_data;
              const groundingArtifact =
                extractGroundingArtifactFromResponseData(responseData, task.taskId) ||
                extractGroundingArtifactFromResponseData(status?.data, task.taskId);
              const answer = responseData?.answer || groundingArtifact?.markdown;

              if (answer) {
                llmResponse.answer = answer;
                llmResponse.sources = responseData?.sources;
                llmResponse.id = responseData?.id ?? responseData?.chat_history_id ?? status.data.id;
                llmResponse.grounding_task_id = task.taskId;
                llmResponse.groundingArtifact = groundingArtifact;
                llmResponse.trace_url =
                  typeof status?.data?.trace_url === "string" && status.data.trace_url.trim().length > 0
                    ? status.data.trace_url
                    : null;

                setGeneratedSections((prevSections) =>
                  prevSections.map((s) => {
                    if (s.id !== section.id) return s;
                    return {
                      ...s,
                      agentUpdates: appendTaskUpdateIfNew(s.agentUpdates || [], {
                        message: "Task complete.",
                        status: "completed",
                        task_id: task.taskId,
                      }),
                    };
                  })
                );
              } else {
                throw new Error("Agent task completed without response data");
              }
            } else if (taskStatus === "failed" || taskStatus === "error") {
              throw new Error(status?.data?.error_message || "Agent task failed");
            } else {
              await handleAgentStream({ task_id: task.taskId }, section, llmResponse);
            }
          } catch (streamError) {
            Logger.error("SectionPage.tsx: Agent reconnection failed:", section.id, streamError);
            const failureMessage = streamError?.message || "Reconnection failed. Please try again.";
            setGeneratedSections((prev) =>
              prev.map((s) =>
                s.id === section.id
                  ? releaseSectionTaskOnFailure(s, failureMessage, task.taskId)
                  : s
              )
            );
            setOutputDecision((prev) => ({
              ...prev,
              [section.id]: true,
            }));
            return;
          }

          const key = `${section.insertion_key}`;
          if (insertionLockRef?.current.has(key)) {
            Logger.debug(`SectionPage.tsx: Skipping resumeAgenticTask insertion for key ${key} - already locked.`);
            return;
          }
          insertionLockRef?.current.add(key);
          try {
            await declineChanges(section.insertion_key);
            await handleAIOutput(llmResponse, section, true);
          } catch (error) {
            if (isMissingSectionBookmarkError(error)) {
              message.warning(error.message);
              setGeneratedSections((prevSections) =>
                prevSections.map((s) =>
                  s.id === section.id
                    ? {
                        ...s,
                        activeSectionTask: undefined,
                        chatAsyncInProgress: false,
                        chatAsyncRegenerationInProgress: false,
                        agenticInProgress: false,
                        agenticRegenerationInProgress: false,
                      }
                    : s
                )
              );
              return;
            }

            Logger.error("SectionPage.tsx: Agentic insertion failed for", section.id, error);
            handleFailedInsertion(section, task);
          } finally {
            insertionLockRef?.current.delete(key);
          }
        };

        try {
          setSectionGeneration(true);
          await Promise.all(
            resumableTasks.map(({ section, task }) =>
              task.kind === "chat_async" ? resumeChatAsyncTask(section, task) : resumeAgenticTask(section, task)
            )
          );

          Logger.debug("SectionPage.tsx: Section task reconnection check complete.");
        } catch (error) {
          Logger.error("SectionPage.tsx: Error during section reconnection process:", error);
          agentReconnectionAttempted.current = false;
          setShowError({
            isVisible: true,
            type: "error",
            title: titleMap[error.name] || "Reconnection Error",
            message: error.name,
            details: { message: error.message, stack: error.stack },
          });
        } finally {
          setSectionGeneration(false);
          agentReconnectionAttempted.current = false;
          if (resumableTasks.length > 0) {
            scrollToLastGenerated();
          } else {
            const panel = document.querySelector(".section_generator_panel_content");
            if (panel) panel.scrollTop = 0;
            const home = document.querySelector(".home_content");
            if (home) home.scrollTop = 0;
          }
        }
      };

      resumeAgentTasks();
    }
  }, [selectedProject, isOnline, sectionGeneration]);

  function getAgentInfoBySectionId(sectionId: string) {
    // TODO:
    // Start agent and subscribe backend to agent

    Logger.debug("SectionPage.tsx:", sectionId);

    // Get WebSocket URL from backend
    const wsUrl = "ws://localhost:8080";
    return { wsUrl };
  }

  // Handles the generation of selected sections
  // Handles the regeneration of selected text
  const handleSpecificRegeneration = async () => {
    try {
      const { selectedText, closestBookmark } = await getSelectedText();
      if (!selectedText) return;

      setRegenerateDialogContent((prev) => ({
        ...prev,
        chat_id: messageChatId,
        section: { display_name: "Custom Selection", content: selectedText, insertion_key: null },
        freeform: "",
        options: [],
      }));
      setSectionDialogContent({ section: { display_name: "Custom Selection" } });
    } catch (error) {
      Logger.error("SectionPage.tsx: Error during specific regeneration:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: axios.isAxiosError(error)
            ? {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                statusText: error.response?.statusText,
                responseData: error.response?.data?.detail,
              }
            : error.name === "RichApi.Error"
            ? {
                errorLocation: error.debugInfo?.errorLocation,
                statement: error.debugInfo?.statement,
                surroundingStatements: error.debugInfo?.surroundingStatements,
              }
            : undefined,
        },
      });
    }
  };

  // Scrolls to the top of the item with the given id
  const scrollToItemTop = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Deletes an attached file and its associated tag
  const deleteTagsFiles = async (settings_group_id: string) => {
    const section_uploaded_files = uploadedFiles.filter((file) => file.sg_id === settings_group_id);
    for (const file of section_uploaded_files) {
      await deleteDocument(file.doc_id);
      await deleteTag(file.tag_id);
    }
  };

  // Scrolls to the last section added to generatedSections
  const scrollToLastGenerated = () => {
    const lastId = generatedSections[generatedSections.length - 1]?.id;
    setTimeout(() => {
      scrollToItemTop(lastId);
    }, 500);
  };

  return (
    <div className="main_content">
      <ProjectDetails
        selectedProject={selectedProject}
        selectedReportTemplate={selectedReportTemplate}
        projectDetailsExpanded={projectDetailsExpanded}
        setProjectDetailsExpanded={setProjectDetailsExpanded}
        projectPlaceholderDetails={projectPlaceholderDetails}
      />
      <SectionGeneratorPanel
        sections={sections}
        templateTagsMap={templateTagsMap}
        setSelectAllSections={setSelectAllSections}
        selectAllSections={selectAllSections}
        generatedSections={generatedSections}
        selectedSections={selectedSections}
        setSelectedSections={setSelectedSections}
        sectionStatus={sectionStatus}
        sectionEvalStatus={sectionEvalStatus}
        setSectionEvalStatus={setSectionEvalStatus}
        outputDecision={outputDecision}
        setOutputDecision={setOutputDecision}
        acceptChanges={acceptChanges}
        runTask={runTask}
        scrollToSection={scrollToSection}
        setSectionDialogContent={setSectionDialogContent}
        setSourceDialogOpen={setSourceDialogOpen}
        setSourceDialogContent={setSourceDialogContent}
        setFeedbackDialogOpen={setFeedbackDialogOpen}
        setFeedbackDialogContent={setFeedbackDialogContent}
        setRegenerateDialogContent={setRegenerateDialogContent}
        setRegenerateDialogOpen={setRegenerateDialogOpen}
        setQualityCheckDialogOpen={setQualityCheckDialogOpen}
        setQualityCheckDialogContent={setQualityCheckDialogContent}
        trackChanges={trackChanges}
        handleTrackChangesToggle={handleTrackChangesToggle}
        bookmarks={bookmarks}
        projectDetailsExpanded={projectDetailsExpanded}
        sectionGeneration={sectionGeneration}
        selectedProject={selectedProject}
        selectedReportTemplate={selectedReportTemplate}
        groundingByTaskId={groundingByTaskId}
        groundingLoadingTaskIds={groundingLoadingTaskIds}
        groundingErrorsByTaskId={groundingErrorsByTaskId}
        fetchGroundingArtifact={fetchGroundingArtifact}
        retrieveSourceDocument={retrieveSourceDocument}
        // uploadedFiles={uploadedFiles}
        // setUploadedFiles={setUploadedFiles}
        uploadDocument={uploadDocument}
        deleteDocument={deleteDocument}
        deleteTag={deleteTag}
        regenerateDialogContent={regenerateDialogContent}
        setShowError={setShowError}
        titleMap={titleMap}
        deleteTagsFiles={deleteTagsFiles}
        scrollToLastGenerated={scrollToLastGenerated}
        uploadedAgentFiles={uploadedAgentFiles}
        setUploadedAgentFiles={setUploadedAgentFiles}
        transcribeAudio={transcribeAudio}
        handleSectionGeneration={handleSectionGeneration}
        setIsAgentSectionRegenerationOn={setIsAgentSectionRegenerationOn}
        setAgenticRegenerationRequest={setAgenticRegenerationRequest}
        retrieveDocumentsList={retrieveDocumentsList}
        submitLangfuseFeedback={submitLangfuseFeedback}
        feedbackStates={feedbackStates}
        setFeedbackStates={setFeedbackStates}
        setGeneratedSections={setGeneratedSections}
        logAuditAction={logAuditAction}
        reportTemplateProjectId={reportTemplateProjectId}
        onGraphViewControllerChange={onGraphViewControllerChange}
      />
      <DialogPopup
        insertionLockRef={insertionLockRef}
        selectedProject={selectedProject}
        sourceDialogOpen={sourceDialogOpen}
        setSourceDialogOpen={setSourceDialogOpen}
        sourceDialogContent={sourceDialogContent}
        setSourceDialogContent={setSourceDialogContent}
        sectionDialogContent={sectionDialogContent}
        feedbackDialogOpen={feedbackDialogOpen}
        setFeedbackDialogOpen={setFeedbackDialogOpen}
        feedbackDialogContent={feedbackDialogContent}
        setFeedbackDialogContent={setFeedbackDialogContent}
        regenerateDialogOpen={regenerateDialogOpen}
        setRegenerateDialogOpen={setRegenerateDialogOpen}
        regenerateDialogContent={regenerateDialogContent}
        setRegenerateDialogContent={setRegenerateDialogContent}
        regenerateLoading={regenerateLoading}
        setRegenerateLoading={setRegenerateLoading}
        qualityCheckDialogOpen={qualityCheckDialogOpen}
        setQualityCheckDialogOpen={setQualityCheckDialogOpen}
        qualityCheckDialogContent={qualityCheckDialogContent}
        // setQualityCheckDialogContent={setQualityCheckDialogContent}
        toggleRegenerateOptions={toggleRegenerateOptions}
        // insertTextAtSection={insertTextAtSection}
        parseMarkdownSections={parseMarkdownSections}
        insertAtSection={insertAtSection}
        hasSectionBookmarks={hasSectionBookmarks}
        // createTable={createTable}
        generatedSections={generatedSections}
        setGeneratedSections={setGeneratedSections}
        setOutputDecision={setOutputDecision}
        setSectionEvalStatus={setSectionEvalStatus}
        setSelectedSections={setSelectedSections}
        declineChanges={declineChanges}
        retrieveSourceDocument={retrieveSourceDocument}
        retrievellmOuputWOGroupAsync={retrievellmOuputWOGroupAsync}
        retreiveSourceDocumentMetadata={retreiveSourceDocumentMetadata}
        deleteDocument={deleteDocument}
        deleteTag={deleteTag}
        deleteTagsFiles={deleteTagsFiles}
        setShowError={setShowError}
        titleMap={titleMap}
        setTest={setTest}
        location={location}
        retrieveAgentQuery={retrieveAgentQuery}
        handleAgentStream={handleAgentStream}
        handleAIOutput={handleAIOutput}
        setIsAgentSectionRegenerationOn={setIsAgentSectionRegenerationOn}
        agenticRegenerationRequest={agenticRegenerationRequest}
        setAgenticRegenerationRequest={setAgenticRegenerationRequest}
        setSectionGeneration={setSectionGeneration}
        submitLangfuseFeedback={submitLangfuseFeedback}
        handleCurrentDocxUpload={handleCurrentDocxUpload}
        retrieveDocumentsForImageInsertion={retrieveDocumentsForImageInsertion}
        retrieveDocumentImages={retrieveDocumentImages}
        downloadDocumentImage={downloadDocumentImage}
        setFeedbackStates={setFeedbackStates}
        setSectionStatus={setSectionStatus}
        clearGroundingForSection={clearGroundingForSection}
        tryClaimAgenticRegenerationRequest={tryClaimAgenticRegenerationRequest}
        releaseAgenticRegenerationRequest={releaseAgenticRegenerationRequest}
        logAuditAction={logAuditAction}
        reportTemplateProjectId={reportTemplateProjectId}
        outputDecision={outputDecision}
        setDocumentReadinessNotice={setDocumentReadinessNotice}
        clearDocumentReadinessNotice={clearDocumentReadinessNotice}
        scrollToGeneratedSection={scrollToItemTop}
      />
      <DocumentReadinessModal
        notice={documentReadinessNotice}
        onClose={clearDocumentReadinessNotice}
        projectId={selectedProject?.id}
        projectName={selectedProject?.name}
      />
      <SectionPageButtonContainer
        sectionGeneration={sectionGeneration}
        selectedText={selectedText}
        handleSpecificRegeneration={handleSpecificRegeneration}
        handleSectionGeneration={handleSectionGeneration}
        selectedSections={selectedSections}
        setActiveTabId={setActiveTabId}
        SearchOutlined={SearchOutlined}
      />
    </div>
  );
};

export default SectionPage;
